import React from 'react';

import { getActivePhase, phaseBrickId } from '../../../utils/phases';
import { positionRows } from '../shared/positionRows';
import { BORDER, cell } from '../shared/style';
import TimelineRowBody from '../shared/TimelineRowBody';
import { emptyProjectRows } from './EmptyProjectRows';
import { phaseDatesLabel, phaseInWindow, phaseMetrics, phaseRowHeight } from './phaseMetrics';
import ProjectBars from './ProjectBars';
import ProjectNameCell from './ProjectNameCell';
import { clientIdOfAff, projectSegments } from './projectSegments';

const neverOff = () => false;

// Une sous-ligne par phase visible : cellule nom + zone des jours.
function phaseRow({ client, ph, clientAffs, activePhase, ctx }) {
  const { appState, days, jiraData, maintenanceLabelHours, openModal } = ctx;
  const briqueIds = new Set((ph.briques || []).map((bq) => bq.id));
  const affs = clientAffs.filter(
    (a) => briqueIds.has(a.briqueId) || a.briqueId === phaseBrickId(ph.id),
  );
  const { segs, nLanes } = projectSegments({ affs, client, appState, jiraData, ...ctx.window });
  const metrics = phaseMetrics(ph, client, appState, jiraData, maintenanceLabelHours);
  const rowKey = `${client.id}|${ph.id}`;
  const datesTxt = phaseDatesLabel(ph);
  const rowH = phaseRowHeight(nLanes, metrics.vendu, datesTxt);
  const nameStyle = cell({
    background: '#fff',
    borderRight: BORDER,
    borderTop: BORDER,
    minHeight: rowH,
    overflow: 'hidden',
    position: 'relative',
  });
  // Glisser sur la ligne : affecter des membres à cette phase sur la période.
  const onSelect = (debut, fin) =>
    openModal(
      'affproj',
      { clientId: client.id, clientName: client.nom, phaseId: ph.id, phaseName: ph.nom },
      {
        clientId: client.id,
        phaseId: ph.id,
        briqueId: '',
        devIds: [],
        heuresParJour: 7,
        debut,
        fin,
      },
    );
  const name = (
    <ProjectNameCell
      key={rowKey}
      client={client}
      ph={ph}
      isActive={!activePhase || activePhase.id === ph.id}
      metrics={metrics}
      datesTxt={datesTxt}
      style={nameStyle}
    />
  );
  const body = (
    <TimelineRowBody
      key={rowKey}
      match={{ rowKey }}
      notWork={neverOff}
      onSelect={onSelect}
      style={{ gridColumn: `span ${days.length}`, borderTop: BORDER, minHeight: rowH }}
    >
      <ProjectBars segs={segs} rowKey={rowKey} />
    </TimelineRowBody>
  );
  return { name, body, rowH };
}

/* Lignes de la Timeline, vue par projet : une ligne par phase en cours sur la fenêtre, projets
   triés par nom. */
export function buildProjectRows(ctx) {
  const { appState, days, COL } = ctx;
  const firstIso = days[0].iso,
    lastIso = days[days.length - 1].iso;
  const projectClients = (appState.clients || [])
    .filter((c) => (c.phases || []).length > 0)
    .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
  if (projectClients.length === 0) return emptyProjectRows(days.length * COL);
  const withWindow = { ...ctx, window: { firstIso, lastIso } };
  const rows = projectClients.flatMap((client) => {
    const phases = (client.phases || []).filter((ph) => phaseInWindow(ph, firstIso, lastIso));
    if (phases.length === 0) return []; // aucune phase en cours sur la période → projet masqué
    const activePhase = getActivePhase(client);
    const clientAffs = appState.affectations.filter(
      (a) =>
        clientIdOfAff(appState.clients, a) === client.id && a.fin >= firstIso && a.debut <= lastIso,
    );
    return phases.map((ph) => phaseRow({ client, ph, clientAffs, activePhase, ctx: withWindow }));
  });
  return positionRows(rows, days.length * COL);
}
